import { useEffect, useRef, useState } from "react";
import { X, Copy, CheckCircle2, QrCode, Loader2 } from "lucide-react";
import { smsApi, reais } from "./api";

// Regra unica, espelhada do backend (que valida de novo): minimo R$ 99,00.
// A conversao em envios arredonda PRA BAIXO — R$ 100,00 = 1.010 envios e o
// resto de centavos nao vira meio envio.
const MINIMO_CENTAVOS = 9900;
const PRESETS = [9900, 24750, 49500, 99000];
const POLL_MS = 4000;

const enviosDe = (centavos) => Math.trunc((centavos * 10) / 99);

/**
 * Popup de adicionar saldo: escolhe o valor, gera o PIX no Mercado Pago e
 * fica consultando ate' aprovar. O credito e' idempotente no backend, entao o
 * loop de consulta nao corre risco de creditar duas vezes.
 */
export default function RecargaPopup({ onFechar, onCreditado }) {
  const [valor, setValor] = useState(PRESETS[0]);
  // Digitos crus do campo livre: mascara de dinheiro que preenche do centavo
  // pro real ("9" -> R$ 0,09; "9900" -> R$ 99,00).
  const [digitos, setDigitos] = useState("");
  const [pix, setPix] = useState(null);
  const [gerando, setGerando] = useState(false);
  const [statusPag, setStatusPag] = useState(null);
  const [copiado, setCopiado] = useState(false);
  const [erro, setErro] = useState(null);
  const timer = useRef(null);

  const customCentavos = digitos ? parseInt(digitos, 10) : null;
  const valorEscolhido = customCentavos ?? valor;
  const customValido = customCentavos === null || customCentavos >= MINIMO_CENTAVOS;

  const gerar = async () => {
    setGerando(true); setErro(null);
    try {
      const p = await smsApi.criarRecarga(valorEscolhido);
      setPix(p); setStatusPag("pending");
    } catch (e) {
      setErro(e?.response?.data?.detail || "Não foi possível gerar o PIX. Tente novamente.");
    } finally {
      setGerando(false);
    }
  };

  useEffect(() => {
    if (!pix || statusPag === "approved") return undefined;
    timer.current = setInterval(async () => {
      try {
        const r = await smsApi.statusRecarga(pix.mp_payment_id);
        setStatusPag(r.status);
        if (r.status === "approved") {
          clearInterval(timer.current);
          onCreditado?.(r.saldo_creditos);
        }
        if (r.status === "valor_irregular") {
          clearInterval(timer.current);
          setErro(r.detalhe || "Pagamento com valor irregular — fale com o suporte.");
        }
      } catch { /* rede oscilou; proxima volta tenta de novo */ }
    }, POLL_MS);
    return () => clearInterval(timer.current);
  }, [pix, statusPag, onCreditado]);

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(pix.copia_cola);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch { window.prompt("Copie o código PIX:", pix.copia_cola); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onFechar(); }}>
      <div className="w-full max-w-md rounded-2xl border bg-background shadow-2xl">
        <div className="flex items-center justify-between border-b px-5 py-3.5">
          <h3 className="font-semibold flex items-center gap-2">
            <QrCode className="w-4 h-4 text-primary" /> Adicionar saldo
          </h3>
          <button onClick={onFechar} aria-label="Fechar"
            className="rounded-lg p-1.5 hover:bg-muted transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!pix ? (
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {PRESETS.map((v) => (
                <button key={v} onClick={() => { setValor(v); setDigitos(""); }}
                  className={`rounded-xl border p-3 text-left transition-colors ${
                    valorEscolhido === v && !digitos ? "border-primary ring-1 ring-primary" : "hover:border-primary"}`}>
                  <p className="font-bold tabular-nums">{reais(v)}</p>
                  <p className="text-xs text-muted-foreground tabular-nums">
                    {enviosDe(v).toLocaleString("pt-BR")} envios
                  </p>
                </button>
              ))}
            </div>

            <div>
              <label className="text-xs text-muted-foreground">Outro valor</label>
              <input value={digitos ? reais(customCentavos) : ""}
                onChange={(e) => setDigitos(e.target.value.replace(/\D/g, "").replace(/^0+/, "").slice(0, 9))}
                placeholder="R$ 0,00" inputMode="numeric"
                className={`mt-1 w-full rounded-lg border bg-background px-3 py-2 text-sm tabular-nums ${
                  digitos && !customValido ? "border-destructive" : ""}`} />
              {digitos && !customValido && (
                <p className="text-xs text-destructive mt-1">
                  O depósito mínimo é {reais(MINIMO_CENTAVOS)}.
                </p>
              )}
              {digitos && customValido && (
                <p className="text-xs text-emerald-500 mt-1 tabular-nums">
                  = {enviosDe(customCentavos).toLocaleString("pt-BR")} envios
                </p>
              )}
            </div>

            {erro && <p className="text-sm text-destructive">{erro}</p>}

            <button onClick={gerar} disabled={gerando || !customValido}
              className="w-full rounded-lg bg-primary text-primary-foreground py-2.5 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40">
              {gerando ? <Loader2 className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4" />}
              {gerando ? "Gerando PIX..." : `Gerar PIX de ${reais(valorEscolhido)}`}
            </button>
            <p className="text-[11px] text-muted-foreground text-center">
              Depósito mínimo de {reais(MINIMO_CENTAVOS)}. O crédito entra assim que o
              pagamento for confirmado.
            </p>
          </div>
        ) : statusPag === "approved" ? (
          <div className="p-8 text-center space-y-3">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
            <p className="font-semibold text-lg">Pagamento confirmado!</p>
            <p className="text-sm text-muted-foreground tabular-nums">
              {pix.envios.toLocaleString("pt-BR")} envios adicionados à sua carteira.
            </p>
            <button onClick={onFechar}
              className="rounded-lg bg-primary text-primary-foreground px-6 py-2 font-semibold">
              Concluir
            </button>
          </div>
        ) : (
          <div className="p-5 space-y-4 text-center">
            <p className="text-sm">
              PIX de <strong className="tabular-nums">{pix.valor_formatado}</strong> ={" "}
              <strong className="tabular-nums">{pix.envios.toLocaleString("pt-BR")} envios</strong>
            </p>
            {pix.qr_base64 && (
              <img src={`data:image/png;base64,${pix.qr_base64.replace(/^data:image\/[a-z]+;base64,/, "")}`}
                alt="QR Code do PIX" className="w-52 h-52 mx-auto rounded-xl border bg-white p-2" />
            )}
            <button onClick={copiar}
              className="w-full rounded-lg border px-4 py-2.5 text-sm font-medium inline-flex items-center justify-center gap-2 hover:border-primary">
              {copiado ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
              {copiado ? "Copiado!" : "Copiar código PIX"}
            </button>
            {erro ? (
              <p className="text-sm text-destructive">{erro}</p>
            ) : (
              <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Aguardando o pagamento — confirma sozinho em segundos.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
